import {useState, useEffect, useRef, useCallback} from 'react';
import {useDispatch} from "react-redux";
import {
    RequestGuessAnalyseMetadata,
    RequestReloadActivePovResults,
    RequestUpdateAnalyseMetadata,
} from "../../../Actions/review.action";
import { normalizeMetadataDraft } from "../../../utils/metadataNormalizer";
import {getAnalysisPov, isSamePov} from "../../../utils/pov";
import './ReviewPage.css'

const normalizeMetadata = (m) => normalizeMetadataDraft(m || {});

export const useMetadataManagement = (
    isCreate,
    id_review,
    stage,
    location,
    setRightPanelMode,
    currentVersionId
) => {
    const dispatch = useDispatch();
    const [metadataDraft, setMetadataDraft] = useState({
        context: undefined,
        document_type: undefined,
        industry: [],
        party_role: undefined,
        studied_party_name: undefined,
        jurisdiction: [],
    });
    const [isGuessingMetadata, setIsGuessingMetadata] = useState(false);
    const [isPovTransitioning, setIsPovTransitioning] = useState(false);
    const [povTransitionPartyName, setPovTransitionPartyName] = useState(null);
    const [povResultsError, setPovResultsError] = useState(null);
    const povTransitionRef = useRef(false);

    const metadataInitialValues = normalizeMetadata(metadataDraft);

    // uniquement si le draft est encore "vide"
    const didInitRef = useRef(false);

    useEffect(() => {
        if (isCreate) return;
        if (!stage?.id) return; // attendre que les vraies données arrivent
        if (didInitRef.current) return;

        const next = normalizeMetadata({
            context: stage?.context,
            document_type: stage?.main_document?.document_type,
            industry: stage?.main_document?.industry,
            party_role: stage?.main_document?.party_role,
            studied_party_name: stage?.main_document?.studied_party_name,
            jurisdiction: stage?.main_document?.jurisdiction,
        });

        setMetadataDraft(next);
        didInitRef.current = true;
    }, [isCreate, stage?.id, stage?.context, stage?.main_document?.document_type, stage?.main_document?.industry, stage?.main_document?.party_role, stage?.main_document?.studied_party_name, stage?.main_document?.jurisdiction]);


    const persistMetadata = useCallback(async (values, nextDraft) => {
        const normalizedValues = normalizeMetadataDraft(values || {}, { strict: true });
        if (isCreate) {
            setMetadataDraft(nextDraft || normalizedValues);
            return;
        }

        const previousDraft = metadataDraft;
        const nextPov = getAnalysisPov(normalizedValues);
        const povChanged = !isSamePov(stage?.main_document, nextPov);

        if (povChanged && povTransitionRef.current) return;
        if (povChanged) {
            povTransitionRef.current = true;
            setIsPovTransitioning(true);
            setPovTransitionPartyName(nextPov.studied_party_name);
            setPovResultsError(null);
        }
        setMetadataDraft(nextDraft || normalizedValues);

        try {
            await dispatch(RequestUpdateAnalyseMetadata(id_review, normalizedValues));
            setRightPanelMode("comments");
        } catch (error) {
            if (povChanged && error?.povPersisted) {
                setPovResultsError(error);
                setRightPanelMode("comments");
            } else {
                setMetadataDraft(previousDraft);
            }
            throw error;
        } finally {
            if (povChanged) {
                povTransitionRef.current = false;
                setIsPovTransitioning(false);
                setPovTransitionPartyName(null);
            }
        }
    }, [dispatch, id_review, isCreate, metadataDraft, setRightPanelMode, stage?.main_document]);

    const saveMetadata = useCallback(async (values) => {
        const normalizedValues = normalizeMetadataDraft(values || {}, { strict: true });
        return persistMetadata(normalizedValues, normalizedValues);
    }, [persistMetadata]);

    const changePov = useCallback(async (nextPov) => {
        if (isCreate || povTransitionRef.current) return;
        const savedMetadata = normalizeMetadataDraft({
            context: stage?.context,
            document_type: stage?.main_document?.document_type,
            industry: stage?.main_document?.industry,
            jurisdiction: stage?.main_document?.jurisdiction,
            party_role: nextPov?.party_role,
            studied_party_name: nextPov?.studied_party_name,
        }, {strict: true});
        const nextDraft = {
            ...metadataDraft,
            party_role: savedMetadata.party_role,
            studied_party_name: savedMetadata.studied_party_name,
        };
        return persistMetadata(savedMetadata, nextDraft);
    }, [isCreate, metadataDraft, persistMetadata, stage?.context, stage?.main_document]);

    const retryPovResults = useCallback(async () => {
        if (isCreate || povTransitionRef.current || !currentVersionId) return;
        const activePov = getAnalysisPov(stage?.main_document);
        povTransitionRef.current = true;
        setIsPovTransitioning(true);
        setPovTransitionPartyName(activePov.studied_party_name);
        setPovResultsError(null);
        try {
            await dispatch(RequestReloadActivePovResults(id_review, currentVersionId));
        } catch (error) {
            setPovResultsError(error);
            throw error;
        } finally {
            povTransitionRef.current = false;
            setIsPovTransitioning(false);
            setPovTransitionPartyName(null);
        }
    }, [currentVersionId, dispatch, id_review, isCreate, stage?.main_document]);

    const guessMetadata = async (allowedJurisdictions = []) => {
        if (isCreate || !id_review) return null;

        setIsGuessingMetadata(true);
        try {
            const guessed = await dispatch(RequestGuessAnalyseMetadata(id_review, {
                current_metadata: metadataDraft,
                allowed_values: {
                    jurisdictions: allowedJurisdictions,
                },
            }));
            return guessed;
        } finally {
            setIsGuessingMetadata(false);
        }
    };

    useEffect(() => {
        if (location.state?.metadataDraft) {
            setMetadataDraft(normalizeMetadata(location.state.metadataDraft));
            setRightPanelMode("comments");
        }
    }, [location.state?.metadataDraft, setRightPanelMode]);

    useEffect(() => {
        if (isCreate) {
            setRightPanelMode("comments");
            return;
        }
    }, [isCreate, id_review, setRightPanelMode]);

    return {
        metadataDraft,
        metadataInitialValues,
        setMetadataDraft,
        saveMetadata,
        changePov,
        retryPovResults,
        guessMetadata,
        isGuessingMetadata,
        isPovTransitioning,
        povTransitionPartyName,
        povResultsError,
    };
};
